import {ECMemberAddress, ECMemberPOD} from 'smag-commons/cfg/hierarchy/ec';
import {MemberAccount} from '../views/ec-members/account';
import {isDefined} from '../commons/commons';
import * as moment from 'moment-timezone';
import {Observable} from 'rxjs';
import {first, map} from 'rxjs/operators';
import {ConfigurationService} from './configuration.service';
import {
    ECMember,
    ECMemberPODRelation,
    ECMemberPODRelationType,
    ECRepresentativeAddress
} from 'smag-commons/cfg/hierarchy';
import {AddressExt} from './google-maps-address-autocomplete/google-maps-address-autocomplete.component';
import {ECEconomicsThirdPartyProducerPod, ECPlantTipMode, ECPlantTipType} from 'smag-commons/dto/eco';
import {ECCacvRedistributionMode, ECEconomicsAdvancedUserPod} from 'smag-commons/dto/eco/community';
import {NiceValuePipe} from './shared.pipe';
import {I18NService} from '../i18n/i18n.service';
import {CACV_STRING} from './ec-economics';
import {AbstractControl, FormGroup, ValidationErrors} from '@angular/forms';
import {FormError, mkTextField} from './energy-community';
import {EC_DOCUMENT_TYPE_OTHER} from '../views/ec-members/new-member/new-ec-member.component';
import {MsgBoxService} from './msg-box.service';


export function mkRepresentativeAddress(address: AddressExt): ECRepresentativeAddress | null {
    if (!isDefined(address)) {
        return null;
    }
    const result = {
        street: address.street,
        street_number: address.streetNumber,
        city: address.municipality,
        postal_code: address.postalCode,
        province: address.province
    };
    // country is always defined, from region downwards it might not
    if (!isDefined(address.region)) {
        result.province = address.country;
    } else if (!isDefined(address.province)) {
        result.province = address.country;
        result.city = address.region;
    }
    return result;
}

export function isPasswordExpired(account: MemberAccount): boolean {
    if (!isDefined(account.pwExpiresOn)) {
        return false;
    }
    return moment().valueOf() > account.pwExpiresOn;
}

export function isPodActive(pod: ECMemberPOD, configurationService: ConfigurationService): Observable<boolean> {
    return configurationService.timeConfig.pipe(
        first(),
        map(timeCfg => {
            const hour = moment.tz(timeCfg.time_zone).startOf('hour').valueOf();
            return isDefined(pod.effective_date) && pod.effective_date <= hour && (!isDefined(pod.deactivation_date) || pod.deactivation_date > hour);
        })
    );
}

const BYTES_IN_MB = 1024 * 1000;
const BYTES_IN_KB = 1024;

export function prettySize(size: number) {
    return size > BYTES_IN_MB ? (size / BYTES_IN_MB).toFixed(1) + ' MB' : (size / BYTES_IN_KB).toFixed(1) + ' kB';
}


// cacv and tip for third party producer and advanced user
export interface NamedECEconomicsThirdPartyProducerPod extends ECEconomicsThirdPartyProducerPod {
    name: string;
    namedPlants: NamedECEconomicsThirdPartyProducerPlant[];
    isActive: boolean;
    generatesTip: boolean;
}

export interface NamedECEconomicsThirdPartyProducerPlant {
    name: string;
    isActive: boolean;
    generatesTip: boolean;
}

export interface NamedECEconomicsAdvancedUserPod extends ECEconomicsAdvancedUserPod {
    name: string;
    namedPlants: NamedECEconomicsAdvancedUserPlant[];
    isActive: boolean;
    generatesTip: boolean;
    capitalGrant: boolean;
}

export interface NamedECEconomicsAdvancedUserPlant {
    name: string;
    isActive: boolean;
    generatesTip: boolean;
}

export function arePlantsActive(pod: NamedECEconomicsThirdPartyProducerPod | NamedECEconomicsAdvancedUserPod) {
    return pod.namedPlants.map(plant => plant.isActive).reduce((acc, val) => acc && val, true);
}

export function showDedicatedShare(pod: NamedECEconomicsThirdPartyProducerPod | NamedECEconomicsAdvancedUserPod) {
    return arePlantsActive(pod) && isDefined(pod.net_tip) && isDefined(pod.tip_configs.plant_tip) && isDefined(pod.plant_tip);
}

export function showCacv(pod: NamedECEconomicsThirdPartyProducerPod | NamedECEconomicsAdvancedUserPod) {
    return isDefined(pod.plant_c_acv);
}

export function getTip(pod: NamedECEconomicsThirdPartyProducerPod | NamedECEconomicsAdvancedUserPod, nvp: NiceValuePipe, i18n: I18NService) {
    const tipMode = pod.tip_configs.mode;
    const tipType = pod.tip_configs.type;
    let plantTip: string;
    let tipText: string;
    let tip: string;
    if (tipMode === ECPlantTipMode.PERCENTAGE) {
        plantTip = nvp.transform(pod.tip_configs.plant_tip * 100, '%');
        switch (tipType) {
            case ECPlantTipType.NET:
                tipText = i18n.translate('Net TIP');
                tip = nvp.transform(pod.net_tip, '€');
                break;
            case ECPlantTipType.GROSS:
                tipText = i18n.translate('Gross TIP');
                tip = nvp.transform(pod.gross_tip, '€');
                break;
            case ECPlantTipType.NON_SURPLUS:
                tipText = i18n.translate('Non Surplus TIP');
                tip = nvp.transform(pod.non_surplus_tip, '€');
                break;
        }
    } else if (tipMode === ECPlantTipMode.EURO_MGW) {
        plantTip = nvp.transform(pod.tip_configs.plant_tip, '€/MWh');
        switch (tipType) {
            case ECPlantTipType.NET:
            case ECPlantTipType.GROSS:
                tipText = i18n.translate('Incentive shared energy');
                tip = nvp.transform(pod.gse ? pod.incentive_shared_energy / 1000 : pod.estimated_incentive_shared_energy / 1000, 'MWh', 5);
                break;
            case ECPlantTipType.NON_SURPLUS:
                tipText = i18n.translate('Non Surplus TIP');
                tip = nvp.transform(pod.incentivized_shared_energy_non_surplus / 1000, 'MWh', 5);
                break;
        }
    }
    return `(${tipText} ${tip} * ${plantTip})`;
}

export function getCacv(pod: NamedECEconomicsThirdPartyProducerPod | NamedECEconomicsAdvancedUserPod, cacvMode: ECCacvRedistributionMode, i18n: I18NService) {
    if (cacvMode === ECCacvRedistributionMode.AS_TIP || cacvMode === ECCacvRedistributionMode.PLANTS) {
        switch (pod.tip_configs.type) {
            case ECPlantTipType.NET:
            case ECPlantTipType.NON_SURPLUS:
                return `(${i18n.translate('Net {0}', CACV_STRING)}`;
            case ECPlantTipType.GROSS:
                return `(${i18n.translate('Gross {0}', CACV_STRING)}`;
        }
    } else {
        return `(${CACV_STRING}`;
    }
}

export function getPlantCacv(pod: NamedECEconomicsThirdPartyProducerPod | NamedECEconomicsAdvancedUserPod, cacvMode: ECCacvRedistributionMode, nvp: NiceValuePipe): string {
    if (cacvMode === ECCacvRedistributionMode.PLANTS) {
        return `${formatEuroValue(pod.net_c_acv ?? 0, nvp)}) =`;
    } else if (cacvMode === ECCacvRedistributionMode.REDISTRIBUTION) {
        return `${formatEuroValue(pod.plant_c_acv ?? 0, nvp)}) =`;
    } else if (cacvMode === ECCacvRedistributionMode.AS_TIP) {
        const tipMode = pod.tip_configs.mode;
        const tipType = pod.tip_configs.type;
        if (tipMode === ECPlantTipMode.PERCENTAGE) {
            const plantTip = formatPercentValue(pod.tip_configs.plant_tip ?? 0, nvp);
            let cacvValue = 0;
            if (tipType === ECPlantTipType.GROSS) {
                cacvValue = pod.gross_c_acv;
            } else if (tipType === ECPlantTipType.NET || tipType === ECPlantTipType.NON_SURPLUS) {
                cacvValue = pod.net_c_acv;
            }
            const cacv = formatEuroValue(cacvValue ?? 0, nvp);
            return `${cacv} * ${plantTip}) =`;
        } else if (tipMode === ECPlantTipMode.EURO_MGW) {
            const plantTip = formatEuroValue(pod.plant_tip ?? 0, nvp);
            let tipValue = 0;
            let cacvValue = 0;
            if (tipType === ECPlantTipType.GROSS) {
                tipValue = pod.gross_tip;
                cacvValue = pod.gross_c_acv;
            } else if (tipType === ECPlantTipType.NET) {
                tipValue = pod.net_tip;
                cacvValue = pod.net_c_acv;
            } else if (tipType === ECPlantTipType.NON_SURPLUS) {
                tipValue = pod.non_surplus_tip;
                cacvValue = pod.net_c_acv;
            }
            const tip = formatEuroValue(tipValue ?? 0, nvp);
            const cacv = formatEuroValue(cacvValue ?? 0, nvp);
            return `${cacv} * (${plantTip} / ${tip})) =`;
        }
    } else {
        return `${formatEuroValue(pod.plant_c_acv ?? 0, nvp)}) =`;
    }
}

export function getDedicatedShare(pod: NamedECEconomicsThirdPartyProducerPod | NamedECEconomicsAdvancedUserPod, nvp: NiceValuePipe) {
    return nvp.transform(isDefined(pod.plant_c_acv) ? pod.plant_c_acv + pod.plant_tip : pod.plant_tip, '€');
}

export function formatEuroValue(value: number, nvp: NiceValuePipe) {
    return nvp.transform(value, '€', 2, value < 0);
}

export function formatPercentValue(value: number, nvp: NiceValuePipe) {
    return nvp.transform(value * 100, '%');
}

// see ECMemberPODRelationType
export enum ECMemberRole {
    OWNER = 'owner',
    HOLDER = 'holder',
    OWNER_AND_HOLDER = 'owner_and_holder'
}


export function getPodRelation(member: ECMember, pod: string): ECMemberPODRelationType {
    return member.pod_relations.find(relation => relation.pod === pod)?.type;
}

export function getPodRole(member: ECMember, pod: string): ECMemberRole {
    const relations = member.pod_relations.filter(relation => relation.pod === pod).map(relation => relation.type);
    return relationsToRoleConverter(relations);
}

export function roleToRelationConverter(pod: string, role: ECMemberRole): ECMemberPODRelation[] {
    switch (role) {
        case ECMemberRole.OWNER:
            return [{pod, type: ECMemberPODRelationType.OWNER}];
        case ECMemberRole.HOLDER:
            return [{pod, type: ECMemberPODRelationType.HOLDER}];
        case ECMemberRole.OWNER_AND_HOLDER:
            return [{pod, type: ECMemberPODRelationType.HOLDER}, {pod, type: ECMemberPODRelationType.OWNER}];
    }
}

export function roleToRelationTypesConverter(role: ECMemberRole): ECMemberPODRelationType[] {
    switch (role) {
        case ECMemberRole.OWNER:
            return [ECMemberPODRelationType.OWNER];
        case ECMemberRole.HOLDER:
            return [ECMemberPODRelationType.HOLDER];
        case ECMemberRole.OWNER_AND_HOLDER:
            return [ECMemberPODRelationType.HOLDER, ECMemberPODRelationType.OWNER];
    }
}

export function relationsToRoleConverter(relations: ECMemberPODRelationType[]): ECMemberRole {
    if (relations.length === 2) {
        return ECMemberRole.OWNER_AND_HOLDER;
    }
    return relationToRoleConverter(relations[0]);
}

export function relationToRoleConverter(relation: ECMemberPODRelationType): ECMemberRole {
    if (relation === ECMemberPODRelationType.HOLDER) {
        return ECMemberRole.HOLDER;
    }
    if (relation === ECMemberPODRelationType.OWNER) {
        return ECMemberRole.OWNER;
    }
}

export function getMemberRole(membersAndRelations: { [memberId: string]: ECMemberPODRelationType[] }): ECMemberRole {
    const memberIds = Object.keys(membersAndRelations);
    if (memberIds.length === 1) {
        return relationsToRoleConverter(membersAndRelations[memberIds[0]]);
    }
    return relationsToRoleConverter([ECMemberPODRelationType.HOLDER]);
}


export function validateDocumentType(ctrl: AbstractControl): ValidationErrors | null {
    const errs: ValidationErrors = {};
    const ctrls: { [ctrl: string]: AbstractControl } = (ctrl as FormGroup).controls;
    // custom type only mandatory for `OTHER` type documents
    if ((ctrls.type.value === EC_DOCUMENT_TYPE_OTHER)
        && !mkTextField(ctrls.customType.value)) {
        ctrls.customType.setErrors({[FormError.EMPTY]: true});
    } else {
        ctrls.customType.setErrors(null);
    }
    return Object.values(errs).every(err => !err) ? null : errs;
}

interface MimeType {
    name: string;
    type: string;
}

const FILE_MIME_TYPES: MimeType[] = [
    {
        type: 'application/pdf',
        name: 'PDF'
    },
    {
        type: 'image/jpeg',
        name: 'JPG'
    },
    {
        type: 'image/png',
        name: 'PNG'
    }
];

export const MAX_FILE_MB = 2;

export function mimeTypesNames(): string {
    return FILE_MIME_TYPES.map(mt => mt.name).join(', ');
}

export function mimeTypes(): string[] {
    return FILE_MIME_TYPES.map(mt => mt.type);
}

export function validateFile(file: File, msgBoxService: MsgBoxService, i18n: I18NService): boolean {
    if (file.size > MAX_FILE_MB * 1048576) {
        msgBoxService.error(i18n.translate('Size of file {0} must be below {1} MB', file.name, MAX_FILE_MB));
        return false;
    }
    if (!mimeTypes().includes(file.type)) {
        msgBoxService.error(i18n.translate('Accepts only {0}', mimeTypesNames()));
        return false;
    }
    return true;
}




